import React, { useState, useContext, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CartContext } from '../context/CartContext';
import api from '../services/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedPresentation, setSelectedPresentation] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeImage, setActiveImage] = useState(0);
  const [activeTab, setActiveTab] = useState('descripcion');

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/productos/${id}`);
      const productData = response.data;
      
      // ✅ Convertir precio a número si viene como string
      if (productData.precio && typeof productData.precio === 'string') {
        productData.precio = parseFloat(productData.precio);
      }
      
      setProduct(productData);
      
      // ✅ Verificar que presentaciones sea un array
      if (productData.presentaciones && Array.isArray(productData.presentaciones) && productData.presentaciones.length > 0) {
        // Asegurar que los precios extra también sean números
        const presentacionesNormalizadas = productData.presentaciones.map(p => ({
          ...p,
          precio_extra: p.precio_extra ? parseFloat(p.precio_extra) : 0
        }));
        productData.presentaciones = presentacionesNormalizadas;
        setSelectedPresentation(presentacionesNormalizadas[0]);
      }
      
      // Cargar productos relacionados (misma categoría)
      if (productData.categoria) {
        try {
          const relatedRes = await api.get(`/productos?categoria=${encodeURIComponent(productData.categoria)}`);
          const related = relatedRes.data
            .filter(p => p.id !== productData.id)
            .map(p => ({
              ...p,
              precio: typeof p.precio === 'string' ? parseFloat(p.precio) : p.precio
            }))
            .slice(0, 4);
          setRelatedProducts(related);
        } catch (error) {
          console.error('Error cargando productos relacionados:', error);
        }
      }
    } catch (error) {
      console.error('Error cargando producto:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (!product) return;

    // ✅ Asegurar que los precios sean números
    const precioBase = typeof product.precio === 'number' ? product.precio : parseFloat(product.precio) || 0;
    const precioExtra = selectedPresentation?.precio_extra ? 
      (typeof selectedPresentation.precio_extra === 'number' ? selectedPresentation.precio_extra : parseFloat(selectedPresentation.precio_extra) || 0) : 0;
    
    const precioFinal = precioBase + precioExtra;
    const presentacionTexto = selectedPresentation ? ` (${selectedPresentation.tipo})` : '';
    
    addToCart({
      id: product.id,
      name: product.nombre + presentacionTexto,
      price: precioFinal,
      image: product.imagen_portada
    }, quantity);

    alert(`✅ ${quantity} ${quantity === 1 ? 'producto agregado' : 'productos agregados'} al carrito`);
  };

  const incrementQuantity = () => {
    const maxStock = selectedPresentation?.stock || product?.stock || 0;
    if (quantity < maxStock) {
      setQuantity(quantity + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const getStockStatus = () => {
    const stock = selectedPresentation?.stock || product?.stock || 0;
    if (stock > 10) return { text: 'Disponible', class: 'success', icon: '✅' };
    if (stock > 0) return { text: `Últimas ${stock} unidades`, class: 'warning', icon: '⚠️' };
    return { text: 'Agotado', class: 'danger', icon: '❌' };
  };

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  // ✅ Función para formatear precio de forma segura
  const formatPrice = (price) => {
    if (price === undefined || price === null) return '0.00';
    const numPrice = typeof price === 'number' ? price : parseFloat(price) || 0;
    return numPrice.toFixed(2);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando producto...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="product-not-found">
        <span className="not-found-icon">😕</span>
        <h2>Producto no encontrado</h2>
        <p>El producto que buscas no está disponible</p>
        <Link to="/products" className="btn-primary">
          Ver todos los productos
        </Link>
      </div>
    );
  }

  const stockStatus = getStockStatus();
  const precioFormateado = formatPrice(product.precio);

  return (
    <div className="product-detail-page">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <Link to="/">Inicio</Link> / 
        <Link to="/products">Productos</Link> / 
        <Link to={`/products?categoria=${product.categoria?.toLowerCase().replace(/ /g, '-')}`}>
          {product.categoria}
        </Link> / 
        <span>{product.nombre}</span>
      </div>

      <div className="product-detail-container">
        {/* Galería de imágenes */}
        <div className="product-gallery">
          <div className="main-image">
            <img 
              src={activeImage === -1 ? product.imagen_portada : (product.imagenes_extra?.[activeImage] || product.imagen_portada)} 
              alt={product.nombre}
            />
            {product.destacado && (
              <span className="product-badge">⭐ Destacado</span>
            )}
          </div>
          
          {product.imagenes_extra && product.imagenes_extra.length > 0 && (
            <div className="thumbnail-grid">
              <div 
                className={`thumbnail ${activeImage === -1 ? 'active' : ''}`}
                onClick={() => setActiveImage(-1)}
              >
                <img src={product.imagen_portada} alt="Principal" />
              </div>
              {product.imagenes_extra.map((img, index) => (
                <div 
                  key={index}
                  className={`thumbnail ${activeImage === index ? 'active' : ''}`}
                  onClick={() => setActiveImage(index)}
                >
                  <img src={img} alt={`Vista ${index + 1}`} />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Información del producto */}
        <div className="product-info">
          <span className="product-category">{product.categoria}</span>
          <h1 className="product-title">{product.nombre}</h1>
          
          <div className="product-rating">
            <span className="stars">{renderStars(4.5)}</span>
            <span className="reviews-count">(12 reseñas)</span>
          </div>

          <div className="product-price-container">
            <span className="product-price">${precioFormateado} MXN</span>
            {selectedPresentation?.precio_extra > 0 && (
              <span className="price-extra">
                + ${formatPrice(selectedPresentation.precio_extra)} MXN ({selectedPresentation.tipo})
              </span>
            )}
          </div>

          <div className="product-stock">
            <span className={`stock-badge ${stockStatus.class}`}>
              {stockStatus.icon} {stockStatus.text}
            </span>
          </div>

          <div className="product-short-description">
            <p>{product.descripcion_corta}</p>
          </div>

          {/* Presentaciones - Con verificación de array */}
          {product.presentaciones && Array.isArray(product.presentaciones) && product.presentaciones.length > 0 && (
            <div className="product-presentaciones">
              <h3>Presentaciones disponibles</h3>
              <div className="presentaciones-grid">
                {product.presentaciones.map((pres, index) => {
                  const precioTotal = (product.precio || 0) + (pres.precio_extra || 0);
                  return (
                    <div
                      key={index}
                      className={`presentacion-card ${selectedPresentation === pres ? 'selected' : ''}`}
                      onClick={() => {
                        setSelectedPresentation(pres);
                        setQuantity(1);
                      }}
                    >
                      <span className="presentacion-tipo">{pres.tipo}</span>
                      <span className="presentacion-precio">
                        ${formatPrice(precioTotal)} MXN
                      </span>
                      <span className="presentacion-stock">
                        Stock: {pres.stock || product.stock}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Cantidad y compra */}
          <div className="product-purchase">
            <div className="quantity-selector">
              <button 
                onClick={decrementQuantity} 
                className="quantity-btn"
                disabled={quantity <= 1}
              >
                -
              </button>
              <span className="quantity">{quantity}</span>
              <button 
                onClick={incrementQuantity} 
                className="quantity-btn"
                disabled={quantity >= (selectedPresentation?.stock || product.stock)}
              >
                +
              </button>
            </div>

            <button 
              onClick={handleAddToCart} 
              className="add-to-cart-btn"
              disabled={(selectedPresentation?.stock || product.stock) <= 0}
            >
              <span>🛒</span>
              {(selectedPresentation?.stock || product.stock) > 0 
                ? 'Agregar al Carrito' 
                : 'Sin Stock'
              }
            </button>
          </div>

          {/* Información adicional */}
          <div className="product-extra-info">
            <div className="info-item">
              <span>🚚</span>
              <div>
                <strong>Envío gratis</strong>
                <p>En compras mayores a $500 MXN</p>
              </div>
            </div>
            <div className="info-item">
              <span>🔄</span>
              <div>
                <strong>Devolución gratis</strong>
                <p>30 días para cambios</p>
              </div>
            </div>
            <div className="info-item">
              <span>🔒</span>
              <div>
                <strong>Pago seguro</strong>
                <p>Tus datos protegidos</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs de información */}
      <div className="product-tabs">
        <div className="tabs-header">
          <button 
            className={`tab-btn ${activeTab === 'descripcion' ? 'active' : ''}`}
            onClick={() => setActiveTab('descripcion')}
          >
            Descripción
          </button>
          <button 
            className={`tab-btn ${activeTab === 'especificaciones' ? 'active' : ''}`}
            onClick={() => setActiveTab('especificaciones')}
          >
            Especificaciones
          </button>
        </div>

        <div className="tabs-content">
          {activeTab === 'descripcion' && (
            <div className="tab-panel">
              <h3>Descripción del producto</h3>
              <p>{product.descripcion_larga}</p>
              <p className="product-origen">
                <strong>Origen:</strong> {product.origen || 'México'}
              </p>
            </div>
          )}

          {activeTab === 'especificaciones' && (
            <div className="tab-panel">
              <h3>Especificaciones</h3>
              <table className="specs-table">
                <tbody>
                  {product.origen && (
                    <tr><td className="spec-label">Origen:</td><td className="spec-value">{product.origen}</td></tr>
                  )}
                  {product.tostado && (
                    <tr><td className="spec-label">Tostado:</td><td className="spec-value">{product.tostado}</td></tr>
                  )}
                  {product.categoria && (
                    <tr><td className="spec-label">Categoría:</td><td className="spec-value">{product.categoria}</td></tr>
                  )}
                  <tr><td className="spec-label">Stock:</td><td className="spec-value">{product.stock} unidades</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Productos relacionados */}
      {relatedProducts.length > 0 && (
        <div className="related-products-section">
          <h2>Productos relacionados</h2>
          <div className="related-products-grid">
            {relatedProducts.map(product => (
              <div 
                key={product.id} 
                className="related-product-card"
                onClick={() => navigate(`/product/${product.id}`)}
              >
                <div className="related-product-image">
                  <img src={product.imagen_portada} alt={product.nombre} />
                </div>
                <div className="related-product-info">
                  <h3>{product.nombre}</h3>
                  <p className="related-product-price">${formatPrice(product.precio)} MXN</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;